import { uniq, maxBy, groupBy } from 'lodash';
import {
  ISignatureRequirement,
  IGroupedSignatureRequirement,
  TriggerType,
} from 'models/signature-requirement/types';

/**
 * Get comma-separated unique creator names (AC7).
 * Confirmed: UI is responsible for resolving integer user IDs to display names.
 * Pass `users` from `state.lookups.users` at the call site.
 */
export const getAggregatedCreatedBy = (
  rules: ISignatureRequirement[],
  users: { id: number; display_name: string }[],
): string => {
  const uniqueCreatorIds = uniq(rules.map(r => r.created_by));
  return uniqueCreatorIds
    .map(id => users.find(u => u.id === id)?.display_name ?? `User ${id}`)
    .join(', ');
};

/**
 * Get the most recent modified date from all rules (AC8)
 */
export const getLatestModifiedDate = (rules: ISignatureRequirement[]): string => {
  const latest = maxBy(rules, 'updated_ts');
  return latest?.updated_ts || '';
};

/**
 * Format insurance trigger display (AC6)
 * Example: "BIN 023880 PCN KYPROD1 GROUP KYM01"
 * Omits null values
 */
export const formatInsuranceTrigger = (
  bin: string | null,
  pcn: string | null,
  group: string | null,
): string => {
  const parts: string[] = [];

  if (bin) parts.push(`BIN ${bin}`);
  if (pcn) parts.push(`PCN ${pcn}`);
  if (group) parts.push(`GROUP ${group}`);

  return parts.join(' ');
};

/**
 * Group signature requirement rules by trigger type for display.
 * No client-side filtering is applied — the API already filtered by trigger_type if applicable.
 * Pass `users` from `state.lookups.users` for creator name resolution.
 */
export const groupRulesByTrigger = (
  rules: ISignatureRequirement[],
  users: { id: number; display_name: string }[],
): IGroupedSignatureRequirement[] => {
  const grouped = groupBy(rules, 'trigger_type');

  return Object.entries(grouped).map(([triggerType, children]) => ({
    triggerType: triggerType as TriggerType,
    children,
    createdBy: getAggregatedCreatedBy(children, users),
    lastModified: getLatestModifiedDate(children),
  }));
};
